import type { ViewCollectionContents } from '@view-model/domain/view/ViewCollectionContents';
import { ThemeColor } from '@view-model/models/common/color';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { aiWorkingViewIdsAtom } from './aiWorkingViewIdsAtom';

/**
 * 帯の色はビューのテーマカラーで替える。テーマカラーはどれも明るいので、
 * 色付きのビューには白を重ねて明るくする。既定の白いビューでは白が見えないため青灰で暗くする。
 */
const SWEEP_STYLES = {
  onWhite: { gradientId: 'ai-working-sweep-gradient-on-white', color: '#A0AEC0', peakOpacity: 0.15 },
  onColor: { gradientId: 'ai-working-sweep-gradient-on-color', color: '#FFFFFF', peakOpacity: 0.55 },
} as const;

/** 帯の幅。ビュー幅に対する比率で持つと、ビューの大小によらず同じ速さに見える。 */
const SWEEP_BAND_RATIO = 0.55;
/** ModelCanvas が描くビュー土台の角丸に合わせる。 */
const VIEW_CORNER_RADIUS = 64;

/** 帯が1周する時間。tailwind.config.js の ai-working-sweep の周期と揃える。 */
const SWEEP_PERIOD_MS = 1050;

type Props = {
  viewContents: ViewCollectionContents | undefined;
};

/**
 * AIが内容を書いている間、対象ビューの上に光の帯を流す。
 * ビューの背景色そのものは変えないので、ビューのテーマカラーは保たれる。
 */
export function AIWorkingViewEffect({ viewContents }: Props) {
  const viewIds = useAtomValue(aiWorkingViewIdsAtom);
  const [visibleViewIds, setVisibleViewIds] = useState<string[]>([]);
  // 0 は performance.now() が返しうる値なので、未表示は null で表す
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (viewIds.length > 0) {
      if (shownAtRef.current === null) shownAtRef.current = performance.now();
      setVisibleViewIds(viewIds);
      return;
    }

    if (shownAtRef.current === null) return;

    const hide = () => {
      shownAtRef.current = null;
      setVisibleViewIds([]);
    };

    // 帯が横切っている途中で消すとちらついて見えるので、周回し終わる区切りまで待つ。
    // 最低でも1周は流し切る（AIの編集が短いと対象ビューが一瞬で消え、帯が横切る前に終わってしまうため）。
    const elapsed = performance.now() - shownAtRef.current;
    const remaining = Math.max(1, Math.ceil(elapsed / SWEEP_PERIOD_MS)) * SWEEP_PERIOD_MS - elapsed;
    if (remaining <= 0) {
      hide();
      return;
    }

    const timer = setTimeout(hide, remaining);
    return () => clearTimeout(timer);
  }, [viewIds]);

  if (visibleViewIds.length === 0 || !viewContents) return null;

  const targets = visibleViewIds.flatMap((viewId) => {
    const rect = viewContents.getRectOf(viewId);
    if (!rect) return [];

    const themeColor = viewContents.findView(viewId)?.themeColor;
    const sweep = themeColor && themeColor !== ThemeColor.White ? SWEEP_STYLES.onColor : SWEEP_STYLES.onWhite;
    return [{ viewId, sweep, ...rect.getXYWH() }];
  });

  if (targets.length === 0) return null;

  return (
    <g className="pointer-events-none motion-reduce:hidden">
      <defs>
        {Object.values(SWEEP_STYLES).map((sweep) => (
          <linearGradient key={sweep.gradientId} id={sweep.gradientId}>
            <stop offset="0" stopColor={sweep.color} stopOpacity={0} />
            <stop offset="0.5" stopColor={sweep.color} stopOpacity={sweep.peakOpacity} />
            <stop offset="1" stopColor={sweep.color} stopOpacity={0} />
          </linearGradient>
        ))}
        {targets.map((target) => (
          <clipPath key={target.viewId} id={clipPathIdOf(target.viewId)}>
            <rect
              x={target.x}
              y={target.y}
              width={target.width}
              height={target.height}
              rx={VIEW_CORNER_RADIUS}
              ry={VIEW_CORNER_RADIUS}
            />
          </clipPath>
        ))}
      </defs>

      {targets.map((target) => {
        const bandWidth = target.width * SWEEP_BAND_RATIO;

        return (
          <g key={target.viewId} clipPath={`url(#${clipPathIdOf(target.viewId)})`}>
            <rect
              x={target.x - bandWidth}
              y={target.y}
              width={bandWidth}
              height={target.height}
              fill={`url(#${target.sweep.gradientId})`}
              className="animate-ai-working-sweep"
              style={{ '--ai-working-sweep-distance': `${target.width + bandWidth}px` } as React.CSSProperties}
            />
          </g>
        );
      })}
    </g>
  );
}

function clipPathIdOf(viewId: string): string {
  return `ai-working-sweep-clip-${viewId}`;
}
